import { Component, computed, effect, inject, signal } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ReportDocument } from '../../../../api/app-types/report-document.app';
import { REPORT_API } from '../../../../api/services/report.service';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrHtmlEditor } from '../../../../core/components/html-editor/html-editor';
import { mergeOriginalTemplateWithEditedContent } from '../../../../core/components/html-editor/template.func';
import { LOADING } from '../../../../core/services/loading.service';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { validateIfIndicatorsAreSet } from '../../../../shared/validators/editor-report-indicators.validator';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { downloadDocumentFiles } from '../../../../shared/helpers/document-download.func';
import { User } from '../../../../api/app-types/user.app';
import { Assignment, AssignmentWithRelations } from '../../../../api/app-types/assignment';
import { REPRESENTATION_MODE } from '../../../../core/services/representation-mode.service';
import { STATE } from '../../../../core/services/state.service';
import {
	canAccessFdsReport,
	flattenAssignments,
	hasQualifyingCaseAccess,
} from '../../../../shared/permissions/fds-report-access.func';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';

@Component({
	selector: 'lvr-fds-assignment-template',
	templateUrl: './fds-assignment-template.html',
	styleUrl: './fds-assignment-template.scss',
	imports: [LvrHeading, LvrHtmlEditor, LvrDefaultActions, LvrPaddingDirective, TranslocoPipe, LvrButton],
})
export class FdsAssignmentTemplate extends RouteInjection {
	private readonly reportApi = inject(REPORT_API);
	private readonly loading = inject(LOADING);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);
	private representationMode = inject(REPRESENTATION_MODE);
	private readonly transloco = inject(TranslocoService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);

	public readonly assignmentId = this.resolveDataAsSignal<number>('assignmentId');
	public readonly reportResolved = this.resolveDataAsSignal<ReportDocument | undefined>('report');
	public readonly assignment = this.resolveDataAsSignal<AssignmentWithRelations | undefined>('assignment');
	private readonly user = this.resolveDataAsSignal<User>('user');
	public readonly report = signal<ReportDocument | null>(null);
	public readonly templateContent = signal<string>('');
	public readonly editorContent = signal<string>('');
	public readonly canEditReportsPermission = hasPermission(this.session.permissions, 'Reports.Edit');
	public readonly canReadReportsPermission = hasPermission(this.session.permissions, 'Reports.Read');
	public readonly renderContent = computed(() => this.editorContent());
	private readonly hasHandledLegacyDownload = signal(false);
	public readonly isRepresentationModeActive = this.representationMode.isActive;
	public readonly representedUserId = this.representationMode.mode().representedUserId;
	private readonly relatedAssignments = computed(() => flattenAssignments(this.state.currentAssignments()));
	public readonly isSaving = this.loading.isCurrentlyLoading('fds-assignment-template.save', 'requestKey');
	public readonly isExporting = this.loading.isCurrentlyLoading('fds-assignment-template.export', 'requestKey');

	public constructor() {
		super();

		// Redirect if user has no access to the report/template
		effect(() => {
			const assignment = this.assignment();

			if (!assignment) return;
			if (this.canAccessTemplate()) return;
			if (this.canEditReportsPermission() && !this.isTemplateAccessContextReady(assignment)) return;

			this.navigateBack();
		});

		effect(() => {
			const id = this.assignmentId();
			const report = this.reportResolved();
			if (report) {
				this.setReport(report);
				return;
			}
			if (!id) return;
			void this.loadTemplate(id);
		});
		effect(() => {
			const report = this.report();
			if (!report || this.hasHandledLegacyDownload()) return;

			const documents = report.documents ?? [];
			if (documents.length < 1) return;

			this.hasHandledLegacyDownload.set(true);
			downloadDocumentFiles(documents);
			this.navigateBack();
		});
	}

	public onTemplateChange(html: string) {
		this.editorContent.set(html);
	}

	public async cancel() {
		await this.navigateBack();
	}

	public async save() {
		if (this.isSaving()) return;

		const doc = this.report();
		const assignmentId = this.assignmentId();
		const template = doc?.template;

		if (!doc || !assignmentId || !template || !this.canEditReport()) return;

		const merged = mergeOriginalTemplateWithEditedContent(template.htmlContent, this.editorContent());

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'fds-assignment-template.save');
		try {
			await this.reportApi.updateReportDocument({
				assignmentId,
				htmlContent: merged,
				reportId: doc.id,
				isFinal: false,
				isDraft: !validateIfIndicatorsAreSet(merged),
				reportDocumentTemplateId: template.id,
			});
			await this.navigateBack();
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.save_fds_template_success')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.save_fds_template_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public async export(fileName: string = 'report.pdf') {
		if (this.isExporting()) return;

		const doc = this.report();
		const assignmentId = this.assignmentId();
		if (!doc || !assignmentId) return;

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'fds-assignment-template.export');
		try {
			const blob = await this.reportApi.getPdfOfReportDocument(assignmentId, doc.id);
			const downloadURL = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadURL;
			link.download = fileName;
			link.click();
			setTimeout(() => link.remove(), 200);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.export_fds_template_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async loadTemplate(assignmentId: number) {
		const docs = await this.reportApi.getDocumentsOfAssignment(assignmentId, 0, 1);
		const doc = docs?.entities?.[0];
		if (!doc) {
			this.report.set(null);
			this.templateContent.set('');
			this.editorContent.set('');
			return;
		}
		this.setReport(doc);
	}

	private setReport(doc: ReportDocument) {
		const templateHtml = doc.template?.htmlContent || '';
		const currentHtml = doc.htmlContent || templateHtml;

		this.report.set(doc);
		this.templateContent.set(templateHtml);
		this.editorContent.set(currentHtml);
	}

	private async navigateBack() {
		const hasReportId = !!this.route.snapshot.paramMap.get('reportId');
		const commands = hasReportId ? ['../../'] : ['../'];
		await this.router.navigate(commands, { relativeTo: this.route });
	}

	private isFdsAssignmentOwner(item: Assignment): boolean {
		const currentUserId = this.user()?.id;
		if (!currentUserId) return false;
		const returnVal = String(currentUserId) === String(item.createdByUser?.id);
		return returnVal;
	}

	private isRepresentingFdsAssignmentOwner(item: Assignment): boolean {
		if (!this.isRepresentationModeActive()) return false;
		if (!this.representedUserId) return false;
		return String(this.representedUserId) === String(item.createdByUser?.id);
	}

	private getEffectiveUserId(): string | number | null | undefined {
		if (this.isRepresentationModeActive()) return this.representedUserId;
		return this.user()?.id;
	}

	private hasQualifyingCaseAccess(assignment: Assignment): boolean {
		return hasQualifyingCaseAccess(assignment, this.relatedAssignments(), this.getEffectiveUserId());
	}

	private isTemplateAccessContextReady(assignment: Assignment): boolean {
		const assignments = this.relatedAssignments();
		return assignments.some((item) => String(item.clientId) === String(assignment.clientId));
	}

	private readonly canAccessTemplate = computed(() => {
		const assignment = this.assignment();

		const hasOwnerLikeAccess =
			!!assignment &&
			(this.isFdsAssignmentOwner(assignment) ||
				this.isRepresentingFdsAssignmentOwner(assignment) ||
				this.hasQualifyingCaseAccess(assignment));

		return canAccessFdsReport({
			assignment,
			relatedAssignments: this.relatedAssignments(),
			effectiveUserId: this.getEffectiveUserId(),
			hasReadPermission: this.canReadReportsPermission(),
			hasEditPermission: this.canEditReportsPermission(),
			hasOwnerLikeAccess,
			roleId: this.user()?.roles?.id,
		});
	});

	public readonly canEditReport = computed(() => {
		const assignment = this.assignment();
		if (!assignment) return false;

		const hasOwnerLikeAccess =
			this.isFdsAssignmentOwner(assignment) || this.isRepresentingFdsAssignmentOwner(assignment);

		const returnVal = hasOwnerLikeAccess && this.canEditReportsPermission();
		return returnVal;
	});
}
